<template>
  <div class="flex flex-col gap-4">
    <NFormItem :show-feedback="false">
      <template #label>
        <div class="flex items-center gap-2">
          <span>{{ t('canvasUI.components.CPdfDimensionsSelector.printSetup') }}</span>
          <div
            v-if="defaultWidth && defaultHeight"
            class="flex items-center gap-1 text-12px"
            :style="{ color: themeVars.text2 }"
          >
            <span
              >{{ t('canvasUI.components.CPdfDimensionsSelector.defaultSetup') }}: {{ defaultWidth }} ×
              {{ defaultHeight }}</span
            >
            <CIcon :icon="defaultOrientation === 'landscape' ? 'image-landscape' : 'image-portrait'" size="12" />
            <CHelpText>
              {{ t('canvasUI.components.CPdfDimensionsSelector.defaultSetupHelp') }}
            </CHelpText>
          </div>
        </div>
      </template>
      <NSelect
        clearable
        :disabled="disabled"
        :options="paperSizeOptions"
        :placeholder="t('canvasUI.components.CPdfDimensionsSelector.selectPaperSize')"
        :render-label="renderPaperSizeLabel"
        :value="props.preset"
        @update:value="handlePresetChange"
      />
    </NFormItem>

    <div v-if="showRatioWarning" class="text-xs -mt-2" :style="{ color: themeVars.text2 }">
      {{ t('canvasUI.components.CPdfDimensionsSelector.pdfRatioWarning') }}
    </div>

    <NFormItem
      v-if="props.preset && props.preset !== 'Custom'"
      :label="t('canvasUI.components.CPdfDimensionsSelector.orientation')"
      :show-feedback="false"
    >
      <NRadioGroup :value="currentOrientation" @update:value="handleOrientationChange">
        <NRadio value="portrait">
          {{ t('canvasUI.components.CPdfDimensionsSelector.portrait') }}
        </NRadio>
        <NRadio value="landscape">
          {{ t('canvasUI.components.CPdfDimensionsSelector.landscape') }}
        </NRadio>
      </NRadioGroup>
    </NFormItem>

    <div v-if="showCustomSize" class="grid grid-cols-[1fr_1fr_100px] gap-3">
      <div class="flex flex-col gap-2">
        <label class="text-14px font-500 leading-1.5">
          {{ t('canvasUI.components.CPdfDimensionsSelector.width') }}
        </label>
        <CInput
          v-model:value="localWidthValue"
          :disabled="disabled"
          :placeholder="t('canvasUI.components.CPdfDimensionsSelector.widthPlaceholder')"
          type="text"
          @update:value="updateWidth"
        />
      </div>
      <div class="flex flex-col gap-2">
        <label class="text-14px font-500 leading-1.5">
          {{ t('canvasUI.components.CPdfDimensionsSelector.height') }}
        </label>
        <CInput
          v-model:value="localHeightValue"
          :disabled="disabled"
          :placeholder="t('canvasUI.components.CPdfDimensionsSelector.heightPlaceholder')"
          type="text"
          @update:value="updateHeight"
        />
      </div>
      <div class="flex flex-col gap-2">
        <label class="text-14px font-500 leading-1.5">
          {{ t('canvasUI.components.CPdfDimensionsSelector.unit') }}
        </label>
        <NSelect
          v-model:value="localUnit"
          class="w-full"
          :disabled="disabled"
          :options="unitOptions"
          @update:value="handleUnitChange"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, h, ref, watch } from 'vue'
import { NFormItem, NRadio, NRadioGroup, NSelect } from 'naive-ui'
import { useI18n } from 'vue-i18n'

import CHelpText from '@lib/components/CHelpText/CHelpText.vue'
import CIcon from '@lib/components/CIcon/CIcon.vue'
import CInput from '@lib/components/CInput/CInput.vue'
import useThemeVars from '@lib/composables/useThemeVars'
import { PAPER_SIZE_PRESETS } from '@canvas-builder/constants/theme.const'
import {
  GOTENBERG_DIMENSION_UNITS,
  GOTENBERG_DIMENSION_PATTERN,
  type GotenbergDimensionUnit,
} from '@lib/constants/dimensions.const'

interface Props {
  preset?: string
  width?: string
  height?: string
  disabled?: boolean
  defaultWidth?: string
  defaultHeight?: string
  showRatioWarning?: boolean
}

interface Emits {
  (e: 'update:preset', value: string | undefined): void
  (e: 'update:width', value: string | undefined): void
  (e: 'update:height', value: string | undefined): void
}

const props = withDefaults(defineProps<Props>(), {
  preset: undefined,
  width: undefined,
  height: undefined,
  disabled: false,
  defaultWidth: undefined,
  defaultHeight: undefined,
  showRatioWarning: false,
})

const emit = defineEmits<Emits>()

const { t } = useI18n()
const themeVars = useThemeVars()

const localUnit = ref<GotenbergDimensionUnit>('mm')
const localWidthValue = ref<string>('')
const localHeightValue = ref<string>('')

const paperSizeOptions = computed(() => [
  ...Object.keys(PAPER_SIZE_PRESETS).map((key) => ({ label: key, value: key })),
  { label: t('canvasUI.components.CPdfDimensionsSelector.customSize'), value: 'Custom' },
])

const unitOptions = computed(() => GOTENBERG_DIMENSION_UNITS.map((unit) => ({ label: unit, value: unit })))

const showCustomSize = computed(() => props.preset === 'Custom')

const defaultOrientation = computed<'portrait' | 'landscape'>(() => {
  if (!props.defaultWidth || !props.defaultHeight) return 'portrait'

  // Extract numeric values to compare
  const dimensionPattern = new RegExp(`^(\\d+(?:\\.\\d+)?)${GOTENBERG_DIMENSION_PATTERN}$`)
  const widthMatch = props.defaultWidth.match(dimensionPattern)
  const heightMatch = props.defaultHeight.match(dimensionPattern)

  if (!widthMatch || !heightMatch) return 'portrait'

  const widthNum = parseFloat(widthMatch[1])
  const heightNum = parseFloat(heightMatch[1])

  return widthNum > heightNum ? 'landscape' : 'portrait'
})

// Compute current orientation based on current width/height values
const currentOrientation = computed<'portrait' | 'landscape'>(() => {
  if (!props.width || !props.height) return 'portrait'

  // Extract numeric values to compare (need to convert to same unit for accurate comparison)
  const dimensionPattern = new RegExp(`^(\\d+(?:\\.\\d+)?)${GOTENBERG_DIMENSION_PATTERN}$`)
  const widthMatch = props.width.match(dimensionPattern)
  const heightMatch = props.height.match(dimensionPattern)

  if (!widthMatch || !heightMatch) return 'portrait'

  // Simple numeric comparison (assumes same unit)
  const widthNum = parseFloat(widthMatch[1])
  const heightNum = parseFloat(heightMatch[1])

  return widthNum > heightNum ? 'landscape' : 'portrait'
})

function renderPaperSizeLabel(option: { label: string; value: string }) {
  if (option.value === 'Custom') {
    return h('div', { class: 'flex items-center justify-between w-full' }, [h('span', option.label)])
  }

  if (option.value in PAPER_SIZE_PRESETS) {
    const preset = PAPER_SIZE_PRESETS[option.value as keyof typeof PAPER_SIZE_PRESETS]
    const width = currentOrientation.value === 'portrait' ? preset.width : preset.height
    const height = currentOrientation.value === 'portrait' ? preset.height : preset.width

    return h('div', { class: 'flex items-center justify-between w-full gap-4' }, [
      h('span', option.label),
      h(
        'span',
        { class: 'ml-auto text-12px text-right', style: { color: themeVars.value.text2 } },
        `${width} × ${height}`,
      ),
    ])
  }

  return h('span', option.label)
}

function handlePresetChange(value: string | null) {
  if (value === null || value === undefined) {
    // User cleared the selection - reset to default
    emit('update:preset', undefined)
    emit('update:width', undefined)
    emit('update:height', undefined)
    return
  }

  emit('update:preset', value)

  if (value !== 'Custom' && value in PAPER_SIZE_PRESETS) {
    const preset = PAPER_SIZE_PRESETS[value as keyof typeof PAPER_SIZE_PRESETS]
    // Use current orientation if already set, otherwise default to portrait
    const orientation = props.width && props.height ? currentOrientation.value : 'portrait'
    if (orientation === 'portrait') {
      emit('update:width', preset.width)
      emit('update:height', preset.height)
    } else {
      emit('update:width', preset.height)
      emit('update:height', preset.width)
    }
  } else if (value === 'Custom') {
    // Reset custom size values
    localWidthValue.value = ''
    localHeightValue.value = ''
    emit('update:width', undefined)
    emit('update:height', undefined)
  }
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
function handleOrientationChange(_value: 'portrait' | 'landscape') {
  // When orientation changes, just swap width and height
  if (props.preset !== 'Custom' && props.width && props.height) {
    emit('update:width', props.height)
    emit('update:height', props.width)
  }
}

function updateWidth() {
  if (localWidthValue.value && localUnit.value) {
    emit('update:width', `${localWidthValue.value}${localUnit.value}`)
  } else if (!localWidthValue.value) {
    emit('update:width', undefined)
  }
}

function updateHeight() {
  if (localHeightValue.value && localUnit.value) {
    emit('update:height', `${localHeightValue.value}${localUnit.value}`)
  } else if (!localHeightValue.value) {
    emit('update:height', undefined)
  }
}

function handleUnitChange() {
  if (showCustomSize.value && (localWidthValue.value || localHeightValue.value)) {
    updateWidth()
    updateHeight()
  }
}

// Initialize custom size values from props
watch(
  [() => props.width, () => props.height, () => props.preset],
  ([width, height, preset]) => {
    if (preset === 'Custom') {
      let widthUnit: string | undefined
      let heightUnit: string | undefined

      const dimensionPattern = new RegExp(`^(\\d+(?:\\.\\d+)?)${GOTENBERG_DIMENSION_PATTERN}$`)

      if (width) {
        const widthMatch = width.match(dimensionPattern)
        if (widthMatch) {
          localWidthValue.value = widthMatch[1]
          widthUnit = widthMatch[2]
        }
      }
      if (height) {
        const heightMatch = height.match(dimensionPattern)
        if (heightMatch) {
          localHeightValue.value = heightMatch[1]
          heightUnit = heightMatch[2]
        }
      }

      // Set the unit - prefer width's unit, but use height's if width is not available
      // If units differ, use the width's unit (component design uses single unit for both dimensions)
      if (widthUnit) {
        localUnit.value = widthUnit as GotenbergDimensionUnit
      } else if (heightUnit) {
        localUnit.value = heightUnit as GotenbergDimensionUnit
      }
    }
  },
  { immediate: true },
)
</script>
